import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { getFlightsByPilot, getUsers, updateFlight, addIssue, updateIssue, uploadFlightFiles } from '../../api';
import { EditFlightPayload, Flight } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { useLoadMore } from '../../hooks/useLoadMore';
import { DateFormat, DateFormatStyle } from '../../utils/DateFormat';
import { formatHours, flightDurationMh } from '../../utils/flightDisplay';
import { getApiErrorMessage } from '../../utils/apiError';
import { canEditFlight, flightPilotOptions, isGeneralUser } from '../../utils/isGeneralUser';
import { uploadIssueFiles } from '../../utils/issueFiles';
import {
  SortConfig,
  defaultDirectionForKind,
  sortByConfig,
  toggleSortConfig,
} from '../../utils/tableSort';
import {
  FLIGHT_SORT_SECONDARY,
  FlightSortKey,
  flightSortKind,
  flightSortValue,
} from '../../utils/flightSort';
import FlightDetailsModal from '../../components/FlightDetailsModal/FlightDetailsModal';
import EditFlightModal from '../../components/EditFlightModal/EditFlightModal';
import LoadMoreButton from '../../components/LoadMoreButton/LoadMoreButton';
import SortableTh from '../../components/SortableTh/SortableTh';
import { parseProfileUserId, profileBasePath } from './profilePaths';
import './ProfilePage.css';
import '../FlightsHistoryPage/FlightsHistoryPage.css';

const ProfileFlightsPage = () => {
  const { userId: userIdParam } = useParams();
  const { currentUserId, user, isAdmin, isTechnician, canFlyOrBook } = useAuth();
  const { aircraftId, current, dataRevision } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const navigate = useNavigate();

  const paramId = parseProfileUserId(userIdParam);
  const targetUserId = paramId ?? currentUserId;
  const isOwn = targetUserId === currentUserId;
  const basePath = profileBasePath(targetUserId, currentUserId);
  const accessDenied =
    (Boolean(userIdParam) && paramId == null) || (!isOwn && !isAdmin);

  const [flights, setFlights] = useState<Flight[]>([]);
  const [users, setUsers] = useState<
    { id: number; name: string; login?: string; role?: string }[]
  >([]);
  const [viewingFlight, setViewingFlight] = useState<Flight | null>(null);
  const [editingFlight, setEditingFlight] = useState<Flight | null>(null);
  const [loading, setLoading] = useState(true);
  const dateFormat: DateFormatStyle =
    user?.date_format === 'long' ? 'long' : 'dot';

  const [sortConfig, setSortConfig] = useState<SortConfig<FlightSortKey>>({
    key: 'date',
    direction: 'desc',
  });

  const editPilotOptions = useMemo(
    () =>
      flightPilotOptions(users, {
        isAdmin,
        currentUserId,
      }),
    [users, isAdmin, currentUserId]
  );

  const loadData = useCallback(async (opts?: { quiet?: boolean }) => {
    if (accessDenied || (isTechnician && isOwn)) return;
    if (!opts?.quiet) setLoading(true);
    try {
      const [flightsRes, usersRes] =
        aircraftId > 0
          ? await Promise.all([getFlightsByPilot(targetUserId), getUsers()])
          : [{ data: [] as Flight[] }, { data: [] }];
      setFlights(flightsRes.data);
      setUsers(usersRes.data);
      setViewingFlight((current) =>
        current
          ? flightsRes.data.find((item) => item.id === current.id) ?? current
          : current
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки полётов'));
    }
    if (!opts?.quiet) setLoading(false);
  }, [
    accessDenied,
    aircraftId,
    targetUserId,
    isTechnician,
    isOwn,
    showError,
  ]);

  useEffect(() => {
    void loadData();
  }, [dataRevision, loadData]);

  const handleSort = (key: FlightSortKey) => {
    const kind = flightSortKind(key);
    const defaultDir =
      key === 'hobbs_end' || key === 'motohours_end' || key === 'id'
        ? 'desc'
        : defaultDirectionForKind(kind);
    setSortConfig((prev) => toggleSortConfig(prev, key, defaultDir));
  };

  const sortedFlights = useMemo(
    () =>
      sortByConfig(
        flights,
        sortConfig,
        flightSortValue,
        flightSortKind,
        FLIGHT_SORT_SECONDARY.filter((s) => s.key !== sortConfig.key)
      ),
    [flights, sortConfig]
  );

  const { visibleCount, hasMore, remaining, nextBatch, loadMore, loadAll } = useLoadMore(
    sortedFlights.length,
    { resetKey: `${sortConfig.key}-${sortConfig.direction}` }
  );
  const visibleFlights = sortedFlights.slice(0, visibleCount);

  const handleSaveEdit = async (
    data: EditFlightPayload,
    extras: {
      tachFiles: File[];
      issueId: number | null;
      issueDescription: string;
      issueSeverity: string;
      issueFiles: File[];
    }
  ) => {
    if (!editingFlight) return;
    try {
      await updateFlight(editingFlight.id, data);
      if (extras.tachFiles.length > 0) {
        await uploadFlightFiles(editingFlight.id, extras.tachFiles);
      }
      if (extras.issueDescription) {
        const selectedPilot = users.find((u) => u.id === data.pilot_id);
        const issueAuthorId =
          selectedPilot && isGeneralUser(selectedPilot)
            ? currentUserId
            : data.pilot_id;
        if (extras.issueId) {
          await updateIssue(extras.issueId, {
            description: extras.issueDescription,
            severity: extras.issueSeverity,
            issue_date: data.flight_date,
          });
          if (extras.issueFiles.length > 0) {
            await uploadIssueFiles(extras.issueId, extras.issueFiles);
          }
        } else {
          const created = await addIssue({
            user_id: issueAuthorId,
            description: extras.issueDescription,
            severity: extras.issueSeverity,
            flight_id: editingFlight.id,
            issue_date: data.flight_date,
          });
          if (extras.issueFiles.length > 0 && created.data.id) {
            await uploadIssueFiles(created.data.id, extras.issueFiles);
          }
        }
      }
      setEditingFlight(null);
      await loadData({ quiet: true });
      showSuccess('Полёт обновлён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка обновления'));
    }
  };

  const personalHours =
    Math.round(flights.reduce((sum, f) => sum + (f.hours || 0), 0) * 100) / 100;
  const personalLandings = flights.reduce(
    (sum, f) => sum + (Number(f.landings) || 0),
    0
  );

  if (accessDenied) {
    return <Navigate to="/profile" replace />;
  }

  if (isTechnician && isOwn) {
    return (
      <div className="profile-page">
        <p className="profile-role-hint">
          Список полётов недоступен для роли «техник».
        </p>
        <div className="profile-back-wrap">
          <Link to="/profile" className="profile-back-link">
            ← Личный кабинет пилота
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {feedbackNode}
      <div className="profile-subpage-header">
        <Link to={basePath} className="profile-back-link">
          ← Личный кабинет пилота
        </Link>
        <h2>{isOwn ? 'Мои полёты' : 'Полёты пилота'}</h2>
        <p className="profile-subpage-hours">
          {isOwn ? 'Личный налёт' : 'Налёт'}:{' '}
          <strong>{formatHours(personalHours)} м/ч</strong>
          {' · '}
          Посадки: <strong>{personalLandings}</strong>
        </p>
      </div>

      {loading ? (
        <p className="profile-role-hint">Загрузка…</p>
      ) : (
        <>
          <div className="flights-history-table-wrap profile-flights-table-wrap">
            <table className="flights-history-table flights-history-table-compact">
              <thead>
                <tr>
                  <SortableTh
                    label="Дата"
                    columnKey="date"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Тип"
                    columnKey="flight_type"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Моточасы"
                    columnKey="hours"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {visibleFlights.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        textAlign: 'center',
                        padding: '30px',
                        color: '#7f8c8d',
                      }}
                    >
                      Нет полётов
                    </td>
                  </tr>
                ) : (
                  visibleFlights.map((f) => (
                    <tr
                      key={f.id}
                      className="flights-history-row"
                      onClick={() => setViewingFlight(f)}
                    >
                      <td>{DateFormat(String(f.date), dateFormat)}</td>
                      <td>{f.flight_type || 'По кругам'}</td>
                      <td>{formatHours(flightDurationMh(f))}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {hasMore ? (
            <LoadMoreButton
              remaining={remaining}
              nextBatch={nextBatch}
              onClick={loadMore}
              onShowAll={loadAll}
            />
          ) : null}
        </>
      )}

      {viewingFlight ? (
        <FlightDetailsModal
          flight={viewingFlight}
          canEdit={canEditFlight(viewingFlight.pilot_id, {
            isAdmin,
            canFlyOrBook,
            currentUserId,
          })}
          canAdmin={false}
          onClose={() => setViewingFlight(null)}
          onEdit={() => {
            setEditingFlight(viewingFlight);
            setViewingFlight(null);
          }}
          onSplit={() => undefined}
          onDelete={() => undefined}
          onOpenIssue={
            viewingFlight.issue
              ? () => navigate(`/issues?flight_id=${viewingFlight.id}`)
              : undefined
          }
        />
      ) : null}

      {editingFlight ? (
        <EditFlightModal
          flight={editingFlight}
          pilots={editPilotOptions}
          defaultFuelLocation={current?.base_airfield?.trim() || ''}
          allowFullSplit={isAdmin}
          onClose={() => setEditingFlight(null)}
          onSave={handleSaveEdit}
        />
      ) : null}
    </div>
  );
};

export default ProfileFlightsPage;
